import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Car, BarChart3, FileText, ShieldCheck, LayoutDashboard, Users, Building2, MessagesSquare, LineChart, UserCog, ScrollText, Languages, Plus, Paintbrush, BookOpen, IdCard, Home as HomeIcon, Star, Bell, Hourglass, FilePen } from "lucide-react";
import { useEffect, useState } from "react";
import { Toaster } from "@/components/ui/sonner";
import { LangProvider, useLang } from "@/lib/i18n";
import { AuthProvider, ConfigProvider, useAuth, can } from "@/lib/auth";
import { Empty } from "@/components/xd/ui";

const Need = ({ any = [], superOnly, children }) => {
  const { user } = useAuth();
  const { t } = useLang();
  const ok = user?.is_super || (!superOnly && any.some((p) => can(user, p)));
  return ok ? children : <Empty text={t("err.forbidden")} />;
};
import PublicLayout from "@/components/xd/PublicLayout";
import { PortalShell, Protected } from "@/components/xd/Portal";
import Home from "@/pages/public/Home";
import Inventory from "@/pages/public/Inventory";
import VehicleDetail from "@/pages/public/VehicleDetail";
import { Sold, Referral, ContentPage } from "@/pages/public/Misc";
import Login from "@/pages/auth/Login";
import { Apply, Reset, ChangePassword } from "@/pages/auth/Apply";
import { BrokerVehicles, BrokerVehicle, BrokerStats, BrokerTerms, BrokerRules, BrokerFavorites } from "@/pages/broker/Broker";
import BrokerHome from "@/pages/broker/BrokerHome";
import { DealerVehicles, DealerVehicleForm, DealerProfile, DealerGuide } from "@/pages/dealer/Dealer";
import { AdminOverview, AdminUsers, AdminAdmins, AdminAudit, AdminContent } from "@/pages/admin/Admin";
import { AdminVehicles, AdminVehicleEdit } from "@/pages/admin/AdminVehicles";
import { AdminVehicleNew, AdminDealer } from "@/pages/admin/AdminManage";
import AdminSite from "@/pages/admin/AdminSite";
import SiteEditor from "@/pages/admin/SiteEditor";
import PageEditor from "@/pages/admin/PageEditor";
import LegalPage from "@/pages/public/LegalPage";
import AdminNotifications, { useUnread } from "@/pages/admin/AdminNotifications";
import { AdminInquiries, AdminAnalytics } from "@/pages/admin/AdminData";

const I = (C) => <C size={16} />;

const BrokerShell = () => {
  const { t } = useLang();
  const nav = [
    { id: "broker-home", to: "/broker", end: true, label: t("nav.home"), icon: I(HomeIcon) },
    { id: "broker-vehicles", to: "/broker/vehicles", label: t("broker.vehicles"), icon: I(Car) },
    { id: "broker-favorites", to: "/broker/favorites", label: t("fav.title"), icon: I(Star) },
    { id: "broker-stats", to: "/broker/stats", label: t("bnav.stats"), icon: I(BarChart3) },
    { id: "broker-terms", to: "/broker/terms", label: t("terms.title"), icon: I(FileText) },
    { id: "broker-rules", to: "/broker/rules", label: t("bnav.rules"), icon: I(ShieldCheck) },
  ];
  return <PortalShell nav={nav} roleLabel={t("role.broker")} />;
};

const DealerShell = () => {
  const { t } = useLang();
  const nav = [
    { id: "dealer-vehicles", to: "/dealer", end: true, label: t("dealer.vehicles"), icon: I(Car) },
    { id: "dealer-pending", to: "/dealer/pending", label: t("dealer.tab_pending"), icon: I(Hourglass) },
    { id: "dealer-drafts", to: "/dealer/drafts", label: t("dealer.tab_drafts"), icon: I(FilePen) },
    { id: "dealer-new", to: "/dealer/new", label: t("dealer.new"), icon: I(Plus) },
    { id: "dealer-profile", to: "/dealer/profile", label: t("profile.title"), icon: I(IdCard) },
    { id: "dealer-guide", to: "/dealer/guide", label: t("dguide.title"), icon: I(BookOpen) },
  ];
  return <PortalShell nav={nav} roleLabel={t("role.dealer")} />;
};

const AdminShell = () => {
  const { t } = useLang();
  const { user } = useAuth();
  const unread = useUnread();
  const all = [
    { id: "admin-overview", to: "/admin", end: true, label: t("admin.overview"), icon: I(LayoutDashboard), show: true },
    { id: "admin-notifications", to: "/admin/notifications", label: t("notif.title"), icon: I(Bell), show: true, badge: unread },
    { id: "admin-vehicles", to: "/admin/vehicles", label: t("admin.vehicles"), icon: I(Car), show: ["vehicles.manage", "vehicles.publish", "prices.set"].some((p) => can(user, p)) },
    { id: "admin-brokers", to: "/admin/brokers", label: t("admin.brokers"), icon: I(Users), show: can(user, "brokers.manage") },
    { id: "admin-dealers", to: "/admin/dealers", label: t("admin.dealers"), icon: I(Building2), show: can(user, "dealers.manage") },
    { id: "admin-inquiries", to: "/admin/inquiries", label: t("admin.inquiries"), icon: I(MessagesSquare), show: can(user, "inquiries.view") },
    { id: "admin-analytics", to: "/admin/analytics", label: t("admin.analytics"), icon: I(LineChart), show: can(user, "analytics.view") },
    { id: "admin-site", to: "/admin/site", label: t("site.title"), icon: I(Paintbrush), show: can(user, "content.manage") || can(user, "content.publish") },
    { id: "admin-content", to: "/admin/content", label: t("admin.content"), icon: I(Languages), show: can(user, "content.manage") },
    { id: "admin-audit", to: "/admin/audit", label: t("admin.audit"), icon: I(ScrollText), show: can(user, "audit.view") },
    { id: "admin-admins", to: "/admin/admins", label: t("admin.admins"), icon: I(UserCog), show: !!user?.is_super },
  ];
  return <PortalShell nav={all.filter((n) => n.show)} roleLabel={user?.is_super ? t("admin.super") : t("admin.admin")} />;
};

const AppToaster = () => {
  const mq = window.matchMedia("(max-width: 767px)");
  const [mobile, setMobile] = useState(mq.matches);
  useEffect(() => {
    const on = (e) => setMobile(e.matches);
    mq.addEventListener("change", on);
    return () => { mq.removeEventListener("change", on); };
  }, [mq]);
  return <Toaster theme="dark" position={mobile ? "bottom-center" : "top-center"} richColors />;
};

function App() {
  return (
    <LangProvider>
      <ConfigProvider>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              <Route element={<PublicLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/inventory" element={<Inventory />} />
                <Route path="/vehicle/:code" element={<VehicleDetail />} />
                <Route path="/sold" element={<Sold />} />
                <Route path="/about" element={<LegalPage pageKey="about" key="about" />} />
                <Route path="/terms" element={<LegalPage pageKey="terms" key="terms" />} />
                <Route path="/privacy" element={<LegalPage pageKey="privacy" key="privacy" />} />
                <Route path="/page/terms" element={<Navigate to="/terms" replace />} />
                <Route path="/page/privacy" element={<Navigate to="/privacy" replace />} />
                <Route path="/page/cookies" element={<Navigate to="/privacy#cookies" replace />} />
                <Route path="/page/about" element={<Navigate to="/about" replace />} />
                <Route path="/page/:key" element={<ContentPage />} />
              </Route>
              <Route path="/r/:uid" element={<Referral />} />
              <Route path="/r/:uid/:code" element={<Referral />} />
              <Route path="/login" element={<Login />} />
              <Route path="/apply/broker" element={<Apply role="broker" />} />
              <Route path="/apply/dealer" element={<Apply role="dealer" />} />
              <Route path="/reset" element={<Reset />} />
              <Route path="/change-password" element={<ChangePassword />} />
              <Route element={<Protected role="broker" />}>
                <Route element={<BrokerShell />}>
                  <Route path="/broker" element={<BrokerHome />} />
                  <Route path="/broker/vehicles" element={<BrokerVehicles />} />
                  <Route path="/broker/favorites" element={<BrokerFavorites />} />
                  <Route path="/broker/v/:code" element={<BrokerVehicle />} />
                  <Route path="/broker/stats" element={<BrokerStats />} />
                  <Route path="/broker/terms" element={<BrokerTerms />} />
                  <Route path="/broker/rules" element={<BrokerRules />} />
                </Route>
              </Route>
              <Route element={<Protected role="dealer" />}>
                <Route element={<DealerShell />}>
                  <Route path="/dealer" element={<DealerVehicles tab="approved" key="approved" />} />
                  <Route path="/dealer/pending" element={<DealerVehicles tab="pending" key="pending" />} />
                  <Route path="/dealer/drafts" element={<DealerVehicles tab="drafts" key="drafts" />} />
                  <Route path="/dealer/new" element={<DealerVehicleForm key="form" />} />
                  <Route path="/dealer/v/:id" element={<DealerVehicleForm key="form" />} />
                  <Route path="/dealer/profile" element={<DealerProfile />} />
                  <Route path="/dealer/guide" element={<DealerGuide />} />
                </Route>
              </Route>
              <Route element={<Protected role="admin" />}>
                <Route element={<AdminShell />}>
                  <Route path="/admin" element={<AdminOverview />} />
                  <Route path="/admin/notifications" element={<AdminNotifications />} />
                  <Route path="/admin/vehicles" element={<AdminVehicles />} />
                  <Route path="/admin/vehicles/new" element={<AdminVehicleNew />} />
                  <Route path="/admin/vehicles/:id" element={<AdminVehicleEdit />} />
                  <Route path="/admin/brokers" element={<Need any={["brokers.manage"]}><AdminUsers role="broker" /></Need>} />
                  <Route path="/admin/dealers" element={<Need any={["dealers.manage"]}><AdminUsers role="dealer" /></Need>} />
                  <Route path="/admin/dealers/:id" element={<Need any={["dealers.manage"]}><AdminDealer /></Need>} />
                  <Route path="/admin/site" element={<SiteEditor />} />
                  <Route path="/admin/site/home" element={<AdminSite />} />
                  <Route path="/admin/site/pages/:key" element={<PageEditor />} />
                  <Route path="/admin/inquiries" element={<Need any={["inquiries.view"]}><AdminInquiries /></Need>} />
                  <Route path="/admin/analytics" element={<Need any={["analytics.view"]}><AdminAnalytics /></Need>} />
                  <Route path="/admin/content" element={<Need any={["content.manage"]}><AdminContent /></Need>} />
                  <Route path="/admin/audit" element={<Need any={["audit.view"]}><AdminAudit /></Need>} />
                  <Route path="/admin/admins" element={<Need superOnly><AdminAdmins /></Need>} />
                </Route>
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
          <AppToaster />
        </AuthProvider>
      </ConfigProvider>
    </LangProvider>
  );
}

export default App;
